var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ChartContentConfig } from './chart-content-config';
import { areEventsReady, isDataHandlerValid, isMarkersHandlerValid, isStorageHandlerValid, isSymbolReady, } from './utils/check';
import { parseParameters, parseSymbol, quoteFeedCallBack } from './utils/parse';
import { defaultMarketDef } from './utils/marketDef';
import { ReplaySubject } from 'rxjs';
import * as moment from 'moment-timezone';
import { defaultXAxisConfig, defaultYAxisConfig } from './utils/axisConfiguration';
/**
 *
 * Abstract class with common and data retrieval methods for charting using chart iq
 *
 * @class AbstractChartContent
 */
var AbstractChartContent = /** @class */ (function () {
    /**
     * @constructor
     * @param {any} containerDiv node element (iframe or div) where the chart is to be located
     * @param {ChartContentConfig} config The config of the chart. Use this config if you want to override certain default config
     */
    function AbstractChartContent(containerDiv, config) {
        if (config === void 0) { config = {}; }
        this.eod = true;
        //public property which can be modified, it is used to store the settings of the chart
        this.chartSettingsKey = 'myChartLayout';
        this._priceType = 'Last';
        this._withEarnings = 1;
        //other options
        this.defaultPriceRelativeInstrument = 20735;
        this.defaultChartType = 'mountain';
        /**
         * If useDelayedQuotesFallback is used, this will be set to true after the chart has been adjusted once
         */
        this.chartRangeAdjustedInitially = false;
        /**
         * If true a message that the shown data are EOD will be displayed
         * @protected
         */
        this._eodFallback = false;
        this.readySub = new ReplaySubject(1);
        this._chartContentConfig = __assign(__assign({}, new ChartContentConfig()), config);
        this._chartContentConfig.xAxis = __assign(__assign({}, defaultXAxisConfig), config.xAxis);
        this._chartContentConfig.yAxis = __assign(__assign({}, defaultYAxisConfig), config.yAxis);
        this.timezone = moment.tz.guess(true);
    }
    /**
     * Attaches a data handler to the chart content, data handler is in charge of retrieving the proper data, streaming and formatting options
     *
     * @param {any} dataHandler
     */
    AbstractChartContent.prototype.attachDataHandler = function (dataHandler) {
        if (!dataHandler || !isDataHandlerValid(dataHandler)) {
            throw new Error('Invalid data handler');
        }
        this._setDefaultDataHandlerBehaviour(dataHandler);
        this._dataHandler = dataHandler;
    };
    /**
     * Attaches an storage handler which takes care of retrieving and saving settings and drawings, optional, default behaviour uses the local storage
     *
     * @param {any} storageHandler
     */
    AbstractChartContent.prototype.attachStorageHandler = function (storageHandler) {
        if (!storageHandler || !isStorageHandlerValid(storageHandler)) {
            throw new Error('Invalid storage handler');
        }
        this._storageHandler = storageHandler;
    };
    /**
     * Attaches a markers handler to the chart content
     *
     * @param {any} markersHandler
     */
    AbstractChartContent.prototype.attachMarkersHandler = function (markersHandler) {
        if (!markersHandler || !isMarkersHandlerValid(markersHandler)) {
            throw new Error('Invalid markers handler');
        }
        this._markersHandler = markersHandler;
    };
    /**
     * Destroy subscriptions for intervals, prices and benchmarks
     *
     * @method destroy
     */
    AbstractChartContent.prototype.destroy = function () {
        this.clearContent(false);
    };
    /**
     * @param {boolean?} persist
     */
    AbstractChartContent.prototype.clearContent = function (persist) {
        if (persist === void 0) { persist = false; }
        this._dataHandler.destroy();
        this._priceType = 'Last';
        this._withEarnings = 1;
        this._idNotation = null;
        this._chartLayout = null;
    };
    Object.defineProperty(AbstractChartContent.prototype, "idNotation", {
        get: function () {
            return this._idNotation;
        },
        /**
         * Sets the idNotation and reinit the chart
         *
         * @param {number} value
         */
        set: function (value) {
            if (this._idNotation !== value) {
                this._idNotation = value;
                this._setData();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractChartContent.prototype, "priceType", {
        get: function () {
            return this._priceType;
        },
        /**
         * Sets the price type and reinit the chart
         *
         * @param {string} value
         */
        set: function (value) {
            var oldValue = this._priceType;
            this._priceType = value;
            if (oldValue !== value) {
                this._sendChangeInformationEvent();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractChartContent.prototype, "withEarnings", {
        get: function () {
            return this._withEarnings;
        },
        /**
         * Sets the earnings value and reinits the chart
         * @param {number} value
         */
        set: function (value) {
            var oldValue = this._withEarnings;
            this._withEarnings = value;
            if (oldValue !== value) {
                this._sendChangeInformationEvent();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AbstractChartContent.prototype, "symbol", {
        /**
         * Symbol of the main notation
         * @returns {string} symbol
         */
        get: function () {
            return (this._idNotation + "#" + this.priceType + "#" + this.withEarnings).toLowerCase();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a chart IQ quote feed object
     *
     * @method getQuoteFeed
     * @returns {any}
     */
    AbstractChartContent.prototype.getQuoteFeed = function () {
        var _this = this;
        return {
            /**
             * This function is called every time the chart needs data,
             * that is when changing the symbol or when changing the granularity
             * @param {any} symbol
             * @param {any} startDate
             * @param {any} endDate
             * @param {any} params
             * @param {any} cb
             */
            fetchInitialData: function (symbol, startDate, endDate, params, cb) {
                _this._fetchData(symbol, startDate, endDate, params, cb);
            },
            /**
             * We retrieve all data once for every instrument, no updates, no pagination, performance seems to be ok
             * @param {any} symbol
             * @param {any} suggestedStartDate
             * @param {any} params
             * @param {any} cb
             */
            fetchUpdateData: function (symbol, suggestedStartDate, params, cb) {
                _this._fetchData(symbol, suggestedStartDate, null, params, cb);
            },
            announceError: function (params, dataCallback) {
                _this._announceError(params, dataCallback);
            },
        };
    };
    /**
     * Sends a message to the chart to change the theme to be used
     * @method _changeTheme
     * @param {string} theme
     */
    AbstractChartContent.prototype.changeTheme = function (theme) {
        //does nothing
    };
    /**
     * Starts the chart IQ engine
     *
     * TODO: use Promise.all for easy reading
     *
     * @method start
     * @returns {Promise<boolean>} resolving to true once the chart engine has been initialized, rejects with an error if something goes wrong
     */
    AbstractChartContent.prototype.start = function () {
        var _this = this;
        return this._getSettingsDrawings().then(function () {
            if (_this._idNotation) {
                return _this._getReferenceData(_this._idNotation)
                    .then(function (data) {
                    _this._referenceData = data;
                    return _this._getTradingScheduleEvents(_this._idNotation).then(function (eventList) {
                        if (!_this._chartContentConfig.manualPriceUpdates) {
                            _this._createPriceSubscription();
                        }
                        return _this._initChartEngine();
                    });
                })
                    .catch(function (error) {
                    console.error(error);
                    return Promise.reject('It is not possible to initialize chart');
                });
            }
            else {
                return _this._initChartEngine();
            }
        });
    };
    AbstractChartContent.prototype._announceError = function (params, dataCallback) {
        console.error('Error while retrieving data.');
        console.error(params);
        console.error(dataCallback);
    };
    /**
     * Returns the trading schedule events in a promise for the passed idNotation
     * @param {number} idNotation
     * @returns {Promise<any>}
     * @private
     *
     */
    AbstractChartContent.prototype._getTradingScheduleEvents = function (idNotation) {
        return this._dataHandler.getTradingScheduleEvents(idNotation);
    };
    /**
     * Returns all settings and drawings in a Promise
     *
     * @returns {Promise<{drawings: any; settings: any}>}
     */
    AbstractChartContent.prototype._getSettingsDrawings = function () {
        if (!this._storageHandler) {
            return Promise.resolve({
                drawings: {},
                settings: {},
            });
        }
        return this._storageHandler.retrieveSettingsDrawings();
    };
    /**
     *  Saves the passed layout
     *
     * @param {any} layout
     * @private
     */
    AbstractChartContent.prototype._saveSettings = function (layout) {
        if (this._storageHandler) {
            this._storageHandler.saveSettings(this.chartSettingsKey, layout);
        }
    };
    /**
     * Removes the chart configuration and the drawings associated to this chart
     *
     * @method _removeChartConfiguration
     */
    AbstractChartContent.prototype._removeChartConfiguration = function () {
        localStorage.removeItem(this.chartSettingsKey);
        if (this._storageHandler) {
            this._storageHandler.removeSettingsDrawings(this.chartSettingsKey, this._idNotation + '#' + this._priceType.toLowerCase() + '#' + this._withEarnings);
        }
    };
    /**
     * Saves the passed drawings
     *
     * @param {any} drawings
     * @private
     */
    AbstractChartContent.prototype._saveDrawings = function (drawings) {
        if (this._storageHandler) {
            this._storageHandler.saveDrawings(this._idNotation + '#' + this._priceType.toLowerCase() + '#' + this._withEarnings, drawings);
        }
    };
    /**
     * Returns a function to use for formatting texts in charts
     *
     * @returns {(value:any, field:FieldConfig, assetClass?:AssetClasses, widgetType?:WidgetTypes)=>string}
     */
    AbstractChartContent.prototype._formatTransform = function () {
        var _this = this;
        return function (value, symbol, field) {
            var resultParams = {};
            parseSymbol(symbol, resultParams);
            var idNotation = resultParams['idNotation'];
            var options = {
                idNotation: idNotation,
                field: field,
            };
            return _this._dataHandler.formatValue(value, options);
        };
    };
    /**
     * Returns a function to use for formatting texts in charts
     *
     * @returns {(value:any, field:FieldConfig, assetClass?:AssetClasses, widgetType?:WidgetTypes)=>string}
     */
    AbstractChartContent.prototype._formatTransformDate = function () {
        var _this = this;
        return function (value, intraday) {
            if (!intraday) {
                intraday = !_this.eod;
            }
            var dateTime = value instanceof Date ? value.getTime() : value;
            return _this._dataHandler.formatDateTime(dateTime, intraday);
        };
    };
    /**
     * Sets the data of the chart
     *
     * @method setData
     */
    AbstractChartContent.prototype._setData = function () {
        var _this = this;
        if (this._idNotation) {
            this._getReferenceData(this._idNotation).then(function (data) {
                _this._getTradingScheduleEvents(_this._idNotation).then(function (eventList) {
                    _this._sendChangeInformationEvent();
                });
            });
        }
    };
    /**
     * Destroys the price subscription
     *
     * @protected
     */
    AbstractChartContent.prototype._destroyPriceSubscription = function () {
        this._dataHandler.destroyPriceSubscription();
    };
    /**
     * Creates a price subscription
     *
     * @protected
     */
    AbstractChartContent.prototype._createPriceSubscription = function () {
        var _this = this;
        this._dataHandler.createMainInstrumentPriceSubscription(this._idNotation, this.priceType, function (update) {
            _this._sendUpdateChartEvent(update, _this._idNotation, '');
        }, function (error) {
            _this._sendErrorToChart(error, _this._idNotation);
        });
    };
    /**
     * Creates subscriptions for the passed benchmark notation.
     *
     * @param {number} idNotation
     * @protected
     */
    AbstractChartContent.prototype._createBenchmarkSubscription = function (idNotation) {
        var _this = this;
        this._dataHandler.createBenchmarkSubscription(idNotation, this.priceType, function (update) {
            _this._sendUpdateChartEvent(update, idNotation, '');
        }, function (error) {
            _this._sendErrorToChart(error, _this._idNotation);
        });
    };
    /**
     * Removes a series when it is removed in the chart
     * @returns {(symbol:any)=>undefined}
     * @protected
     */
    AbstractChartContent.prototype._removeSeries = function () {
        var _this = this;
        return function (symbol) {
            var params = {};
            parseSymbol(symbol, params);
            _this._removeBenchmarkInformation(params['idNotation']);
        };
    };
    /**
     * Removes all staging and subscription data for the passed benchmark if it exist
     *
     * @param {any} idNotation
     * @protected
     */
    AbstractChartContent.prototype._removeBenchmarkInformation = function (idNotation) {
        this._dataHandler.destroyBenchmarkSubscription(idNotation);
    };
    /**
     * Creates a market definition for chart iq based on the event list for this notation
     *
     * @returns {(symbolObject:any)=>any}
     * @protected
     */
    AbstractChartContent.prototype._getMarketFactory = function () {
        var _this = this;
        return function (symbolObject) {
            var marketDef = Object.assign({}, defaultMarketDef);
            var daysOfWeek = {};
            if (isSymbolReady(symbolObject) && areEventsReady(_this._dataHandler.eventList)) {
                var eventList_1 = _this._dataHandler.eventList.eventList;
                marketDef.rules = [];
                //rules are overwritten
                Object.keys(eventList_1)
                    .reverse()
                    .forEach(function (day) {
                    var openRaw = eventList_1[day].openRaw;
                    var closeRaw = eventList_1[day].closeRaw;
                    var open = eventList_1[day].open, close = eventList_1[day].close, openExtra = null, closeExtra = null;
                    if (open === close) {
                        open = '00:00';
                        close = '23:59';
                    }
                    if (openRaw > closeRaw) {
                        // way of handling japan australia
                        open = '00:00';
                        close = eventList_1[day].close;
                        openExtra = eventList_1[day].open;
                        closeExtra = '23:59';
                    }
                    if (!daysOfWeek[eventList_1[day].dayOfWeek]) {
                        //first time dayofweek is set
                        marketDef.rules.push({
                            dayofweek: eventList_1[day].dayOfWeek,
                            open: open,
                            close: close,
                        });
                        daysOfWeek[eventList_1[day].dayOfWeek] = true;
                    }
                    if (openExtra && closeExtra) {
                        //first time dayofweek is set
                        marketDef.rules.push({
                            dayofweek: eventList_1[day].dayOfWeek,
                            open: openExtra,
                            close: closeExtra,
                            name: 'extra',
                        });
                    }
                });
            }
            return marketDef;
        };
    };
    /**
     * Returns the historic prices as an array in a promise for the passed options
     * @param {any} options idNotation rangeType priceType withEarnings
     * @returns {Promise<any[]>}
     * @private
     *
     */
    AbstractChartContent.prototype._getHistoricData = function (options) {
        return this._dataHandler.getHistoricData(options);
    };
    /**
     * Method used for fetching data in the charts, no pagination or updates used
     *
     * @param {any} symbol
     * @param {any} startDate
     * @param {any} endDate
     * @param {any} params
     * @param {any} cb
     * @protected
     *
     */
    AbstractChartContent.prototype._fetchData = function (symbol, startDate, endDate, params, cb) {
        var _this = this;
        try {
            if (!symbol) {
                //error explaining no symbol available
                cb({ error: 'chart-no-symbol' });
            }
            else {
                var paramsToUse_1 = parseParameters(symbol, startDate, endDate, params, this._priceType, this._withEarnings);
                this.eod = paramsToUse_1.rangeType === 'eod';
                this._getReferenceData(paramsToUse_1.idNotation)
                    .then(function (data) {
                    _this._getHistoricData(paramsToUse_1)
                        .then(function (results) {
                        if (!results) {
                            cb({ error: 'chart-no-data' });
                        }
                        if (results.length === 0) {
                            cb({
                                quotes: [],
                                moreAvailable: false,
                            });
                        }
                        if (_this._chartContentConfig.useDelayedQuotesFallback) {
                            var latestDataPoint = results.slice(-1);
                            if (latestDataPoint && latestDataPoint[0] && latestDataPoint[0][0]) {
                                var latestDataPointTime = latestDataPoint[0][0] * 1000;
                                _this._delayedQuotesFallback(latestDataPointTime);
                            }
                        }
                        try {
                            var exchange = 'IDMS';
                            if (_this._eodFallback && _this.eod) {
                                _this._eodFallback = false;
                                exchange = 'EOD';
                            }
                            quoteFeedCallBack(cb, results, _this.eod, _this.timezone, exchange);
                            _this._sendCurrentRecordUpdateChartEvent(_this._extractCurrentRecord(results), paramsToUse_1.idNotation, '');
                            if (_this._idNotation !== paramsToUse_1.idNotation &&
                                !_this._dataHandler.isBenchmarkSubscribed(paramsToUse_1.idNotation) &&
                                !_this._chartContentConfig.manualPriceUpdates) {
                                _this._createBenchmarkSubscription(paramsToUse_1.idNotation);
                            }
                        }
                        catch (err) {
                            //this would prevent infinite loops
                            cb({ error: err });
                        }
                    })
                        .catch(function (error) {
                        // Permission denied for intraday data
                        if (error &&
                            error.reason &&
                            error.reason.value === 6 &&
                            paramsToUse_1.rangeType === 'intraday') {
                            _this._eodFallback = true;
                            cb({
                                quotes: [],
                                attribution: { source: 'IDMS', exchange: 'EOD' },
                            });
                            _this.eodFallback();
                        }
                        else {
                            cb({ error: error });
                        }
                    });
                })
                    .catch(function (error) {
                    cb({ error: error });
                });
            }
        }
        catch (error) {
            cb({ error: error });
        }
    };
    AbstractChartContent.prototype._extractCurrentRecord = function (results) {
        if (results && results[results.length - 1].length === 7) {
            return results[results.length - 1];
        }
        return null;
    };
    /**
     * In order to be able to inject the reference data this method is provided
     * @param {number} idNotation
     * @returns {Promise<any>}
     * @private
     *
     */
    AbstractChartContent.prototype._getReferenceData = function (idNotation) {
        return this._dataHandler.getReferenceData(idNotation);
    };
    /**
     * Reset range to the previous day if intraday of the current day
     * with delayed quotes is set and the data displayed is empty
     * due to market opening times
     * @param {number} latestDataPointTime
     * @param {number} now
     * @returns {boolean}
     * @protected
     */
    AbstractChartContent.prototype._delayedQuotesFallback = function (latestDataPointTime, now) {
        if (this._chartContentConfig.spanBase !== 'today') {
            return false;
        }
        if (!now) {
            now = new Date().getTime();
        }
        var eventDates = Object.keys(this._dataHandler.eventList.eventList);
        /* We need at least 2 dates to make this work */
        if (eventDates.length < 2) {
            return false;
        }
        var eventDateMostRecent = eventDates[0];
        var eventDateSecondMostRecent = eventDates[0];
        eventDates.forEach(function (date) {
            if (date > eventDateMostRecent) {
                eventDateSecondMostRecent = eventDateMostRecent;
                eventDateMostRecent = date;
            }
        });
        var _a = this._dataHandler.eventList.eventList[eventDateMostRecent], openRaw = _a.openRaw, closeRaw = _a.closeRaw;
        /* We are in the market opening time but we don't have data for it yet */
        if (openRaw < now && now < closeRaw && latestDataPointTime < openRaw && !this.chartRangeAdjustedInitially) {
            var from = new Date(this._dataHandler.eventList.eventList[eventDateSecondMostRecent].openRaw);
            var to = new Date(this._dataHandler.eventList.eventList[eventDateSecondMostRecent].closeRaw);
            this.setRange(from, to);
            this.chartRangeAdjustedInitially = true;
            return true;
        }
        return false;
    };
    /**
     *
     * Init the default price rel instrument for the required studies
     *
     * @param {any} data
     * @protected
     */
    AbstractChartContent.prototype._initDefaultPriceRelInstrument = function (data) { };
    /**
     * Sets the default behaviour of the non implemented methods
     * @param {any} dataHandler
     * @private
     */
    AbstractChartContent.prototype._setDefaultDataHandlerBehaviour = function (dataHandler) {
        if (!dataHandler.destroy) {
            dataHandler.destroy = function () { return undefined; };
        }
        if (!dataHandler.destroyBenchmarkSubscriptions) {
            dataHandler.destroyBenchmarkSubscriptions = function () { return undefined; };
        }
        if (!dataHandler.destroyPriceSubscription) {
            dataHandler.destroyPriceSubscription = function () { return undefined; };
        }
        if (!dataHandler.destroyBenchmarkSubscription) {
            dataHandler.destroyBenchmarkSubscription = function () { return undefined; };
        }
        if (!dataHandler.isBenchmarkSubscribed) {
            dataHandler.isBenchmarkSubscribed = function () { return false; };
        }
        if (!dataHandler.getTradingScheduleEvents) {
            dataHandler.getTradingScheduleEvents = function () { return Promise.resolve(null); };
        }
        if (!dataHandler.getReferenceData) {
            dataHandler.getReferenceData = function () { return Promise.resolve(null); };
        }
        if (!dataHandler.translate) {
            dataHandler.translate = function (key, params) { return key; };
        }
        if (!dataHandler.getMarkers) {
            dataHandler.getMarkers = function (idNotation, markerType) { return Promise.resolve(null); };
        }
        if (!dataHandler.formatDateTime) {
            dataHandler.formatDateTime = function (value, iday) { return value; };
        }
        if (!dataHandler.formatValue) {
            dataHandler.formatValue = function (value, options) { return value; };
        }
        if (!dataHandler.createMainInstrumentPriceSubscription) {
            dataHandler.createMainInstrumentPriceSubscription = function (idNotation, priceType, callback) { return undefined; };
        }
        if (!dataHandler.createBenchmarkSubscription) {
            dataHandler.createBenchmarkSubscription = function (idNotation, priceType, callback) { return undefined; };
        }
    };
    return AbstractChartContent;
}());
export { AbstractChartContent };
//# sourceMappingURL=abstract-chart-content.js.map